import { useCallback, useMemo } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, Edit, FileText, ClipboardList, UserRound } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from 'shared/ui/alert'
import { Badge } from 'shared/ui/badge'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Checkbox } from 'shared/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'

import type { PermitListItem } from '../api/permitService'
import {
  PERMIT_STATUS_CODES,
  getPermitStatusBadgeClass,
  getPermitStatusLabel,
  isPrintablePermitStatus,
  normalizePermitStatus,
} from '../constants/permitStatus'
import { DEFAULT_TENANT_ID } from 'shared/utils/tenantContext'

interface PermitIndexProps {
  permits: PermitListItem[]
  isLoading?: boolean
  error?: string | null
  onViewPermit: (permitId: string) => void
  onEditPermit: (permitId: string) => void
  locationLabelMap?: Record<string, string>
  unitLabelMap?: Record<string, string>
  mode?: 'default' | 'approval'
  tenantId?: string
  currentUserType?: string | null
  currentUserId?: string | null
  currentUserName?: string | null
  sortField?: PermitIndexSortField | null
  sortDirection?: 'asc' | 'desc'
  onSort?: (field: PermitIndexSortField) => void
  selectedPermitIds?: Set<string>
  onToggleSelect?: (permitId: string, checked: boolean) => void
  canMassSelect?: boolean
}

export type PermitIndexSortField =
  | 'permitNumber'
  | 'permitType'
  | 'workDescription'
  | 'location'
  | 'requesterName'
  | 'plannedStart'
  | 'status'

type DisplayPermit = {
  recordId: string
  permitNo: string
  permitType: string
  permitTypeClass: string
  workDescription: string
  locationDisplay: string
  unitDisplay: string
  requestor: string
  company: string
  workDateRange: string
  workTimeRange: string
  statusLabel: string
  statusClass: string
  rawStatus: string
  requesterId: string | null
  requesterNameNormalized: string
  createdDate: string
}

const normalizePersonToken = (value?: string | null) => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim().replace(/\s+/g, ' ').toLowerCase()
}

const safeDate = (value?: string | null) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (value?: string | null) => {
  const date = safeDate(value)
  if (!date) return '-'
  return date.toLocaleDateString('th-TH', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

const formatTime = (value?: string | null) => {
  const date = safeDate(value)
  if (!date) return null
  return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
}

const formatDateTime = (value?: string | null) => {
  const date = safeDate(value)
  if (!date) return '-'
  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateRange = (start?: string | null, end?: string | null) => {
  const startDate = formatDate(start)
  const endDate = formatDate(end)

  if (startDate === '-' && endDate === '-') return '-'
  if (startDate !== '-' && endDate !== '-') {
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`
  }
  return startDate !== '-' ? startDate : endDate
}

const formatTimeRange = (start?: string | null, end?: string | null) => {
  const startTime = formatTime(start)
  const endTime = formatTime(end)
  if (!startTime && !endTime) return '-'
  if (startTime && endTime) return `${startTime} - ${endTime}`
  return startTime || endTime || '-'
}

const getPermitTypeBadgeClass = (permitType?: string | null) => {
  const normalizedType = (permitType ?? '').trim().toLowerCase()

  if (normalizedType.includes('hot work')) {
    return 'border-red-200 bg-red-50 text-red-700'
  }

  if (normalizedType.includes('confine')) {
    return 'border-orange-200 bg-orange-50 text-orange-700'
  }

  if (normalizedType.includes('cold work')) {
    return 'border-indigo-200 bg-indigo-50 text-indigo-700'
  }

  return 'border-slate-200 bg-slate-50 text-slate-700'
}

const EMPLOYEE_PRINTABLE_STATUSES = new Set<string>([
  PERMIT_STATUS_CODES.READY_TO_OPEN,
  PERMIT_STATUS_CODES.OPEN,
  PERMIT_STATUS_CODES.CLOSED,
])

export function PermitIndex({
  permits,
  isLoading = false,
  error = null,
  onViewPermit,
  onEditPermit,
  locationLabelMap = {},
  unitLabelMap = {},
  mode = 'default',
  tenantId = DEFAULT_TENANT_ID,
  currentUserType = null,
  currentUserId = null,
  currentUserName = null,
  sortField = null,
  sortDirection = 'asc',
  onSort,
  selectedPermitIds,
  onToggleSelect,
  canMassSelect = false,
}: PermitIndexProps) {
  const normalizedUserType = typeof currentUserType === 'string' ? currentUserType.trim().toUpperCase() : ''
  const isEmployeeUser = normalizedUserType === 'EMPLOYEE'
  const isContractorUser = normalizedUserType === 'CONTRACTOR'
  const normalizedCurrentUserId =
    typeof currentUserId === 'string' && currentUserId.trim().length > 0 ? currentUserId.trim().toLowerCase() : null
  const normalizedCurrentUserName = normalizePersonToken(currentUserName)

  const displayPermits = useMemo<DisplayPermit[]>(() => {
    return (permits ?? []).map((permit) => {
      const locationDisplay = locationLabelMap[permit.location] || permit.locationName || permit.location || '-'
      const unitDisplay = unitLabelMap[permit.unit ?? ''] || permit.unitName || permit.unit || ''

      return {
        recordId: permit.permitId,
        permitNo: permit.permitNumber || permit.permitId,
        permitType: permit.permitType || '-',
        permitTypeClass: getPermitTypeBadgeClass(permit.permitType),
        workDescription: permit.workDescription || '-',
        locationDisplay,
        unitDisplay,
        requestor: permit.requesterName || '-',
        company: permit.requesterCompany || '-',
        workDateRange: formatDateRange(permit.plannedStart, permit.plannedEnd),
        workTimeRange: formatTimeRange(permit.plannedStart, permit.plannedEnd),
        statusLabel: getPermitStatusLabel(permit.status, permit.statusName),
        statusClass: getPermitStatusBadgeClass(permit.status),
        rawStatus: permit.status || '',
        requesterId: typeof permit.requesterId === 'string' ? permit.requesterId.trim().toLowerCase() : null,
        requesterNameNormalized: normalizePersonToken(permit.requesterName),
        createdDate: formatDateTime(permit.createdAt),
      }
    })
  }, [locationLabelMap, permits, unitLabelMap])

  const handleOpenPreview = useCallback(
    (permitId: string) => {
      const activeTenantId = tenantId?.trim() || DEFAULT_TENANT_ID
      if (!activeTenantId) {
        toast.error('ไม่พบ tenantId สำหรับดูรายงาน')
        return
      }
      const previewUrl = `/permit-report-pdf/${encodeURIComponent(permitId)}?tenantId=${encodeURIComponent(activeTenantId)}`
      // Keep the referrer header for security appliances that validate same-origin navigation.
      const previewWindow = window.open(previewUrl, '_blank', 'noopener')
      if (!previewWindow) {
        toast.error('เบราว์เซอร์บล็อกการเปิดแท็บใหม่สำหรับ PDF')
      }
    },
    [tenantId],
  )

  const isApprovalMode = mode === 'approval'
  const showMassSelectColumn = isApprovalMode && canMassSelect
  const tableColumnCount = (isApprovalMode ? 7 : 8) + (showMassSelectColumn ? 1 : 0)

  const renderSortableHead = (label: string, field: PermitIndexSortField) => {
    const isActive = sortField === field
    const Icon = isActive ? (sortDirection === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown

    if (!onSort) {
      return <span>{label}</span>
    }

    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-left font-medium text-slate-700 transition-colors hover:text-slate-900"
        onClick={() => onSort(field)}
      >
        <span>{label}</span>
        <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
      </button>
    )
  }

  return (
    <>
      <Card className="overflow-hidden border border-amber-200 shadow-sm">
        <CardHeader className="flex flex-wrap items-start justify-between gap-1 border-b border-amber-100 bg-amber-50/70 p-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              รายการใบอนุญาตทั้งหมด
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="bg-white p-3">
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="overflow-x-auto rounded-xl">
            <Table>
              <TableHeader className="bg-amber-50/70">
                <TableRow>
                  {showMassSelectColumn ? <TableHead className="w-10" /> : null}
                  <TableHead className="whitespace-nowrap">{renderSortableHead('Permit No', 'permitNumber')}</TableHead>
                  <TableHead className="whitespace-nowrap">{renderSortableHead('รายละเอียดงาน', 'workDescription')}</TableHead>
                  <TableHead className="whitespace-nowrap">{renderSortableHead('พื้นที่ปฏิบัติงาน', 'location')}</TableHead>
                  <TableHead className="whitespace-nowrap">{renderSortableHead('ผู้ขอ', 'requesterName')}</TableHead>
                  <TableHead className="whitespace-nowrap">{renderSortableHead('วันที่ปฏิบัติงาน', 'plannedStart')}</TableHead>
                  <TableHead className="whitespace-nowrap">{renderSortableHead('สถานะ', 'status')}</TableHead>
                  <TableHead className="whitespace-nowrap text-center">
                    {isApprovalMode ? 'อนุมัติรายการ' : 'การดำเนินการ'}
                  </TableHead>
                  {!isApprovalMode ? <TableHead className="whitespace-nowrap text-center">Print</TableHead> : null}
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} className="py-10 text-center text-sm text-gray-500">
                      กำลังโหลดข้อมูล...
                    </TableCell>
                  </TableRow>
                ) : displayPermits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} className="py-10 text-center text-sm text-gray-500">
                      ไม่พบข้อมูลตามเงื่อนไขที่เลือก
                    </TableCell>
                  </TableRow>
                ) : (
                  displayPermits.map((permit) => {
                    const isOwnPermit =
                      (Boolean(normalizedCurrentUserId) &&
                        Boolean(permit.requesterId) &&
                        permit.requesterId === normalizedCurrentUserId) ||
                      (Boolean(normalizedCurrentUserName) &&
                        Boolean(permit.requesterNameNormalized) &&
                        permit.requesterNameNormalized === normalizedCurrentUserName)
                    const canEditPermit = !isEmployeeUser || isOwnPermit
                    const normalizedPermitStatus = normalizePermitStatus(permit.rawStatus)
                    const canPrintPermit = isEmployeeUser
                      ? normalizedPermitStatus !== null && EMPLOYEE_PRINTABLE_STATUSES.has(normalizedPermitStatus)
                      : isPrintablePermitStatus(permit.rawStatus) && (isOwnPermit || isContractorUser)

                    const isSelected = selectedPermitIds?.has(permit.recordId) ?? false

                    return (
                    <TableRow key={permit.recordId} className="hover:bg-amber-50/40">
                      {showMassSelectColumn ? (
                        <TableCell className="w-10 text-center">
                          <Checkbox
                            className="h-5 w-5"
                            checked={isSelected}
                            onCheckedChange={(checked) => onToggleSelect?.(permit.recordId, checked === true)}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="whitespace-nowrap">
                        <div className="font-semibold text-gray-900">{permit.permitNo}</div>
                        <div className="text-xs text-gray-500">สร้างเมื่อ {permit.createdDate}</div>
                        <Badge variant="secondary" className={`mt-1 ${permit.permitTypeClass}`}>
                          {permit.permitType}
                        </Badge>
                      </TableCell>

                      <TableCell className="max-w-[260px] text-sm text-gray-700">{permit.workDescription}</TableCell>

                      <TableCell className="text-sm text-gray-700">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{permit.locationDisplay}</span>
                          {permit.unitDisplay && permit.unitDisplay !== permit.locationDisplay ? (
                            <span className="text-xs text-gray-500">{permit.unitDisplay}</span>
                          ) : null}
                        </div>
                      </TableCell>

                      <TableCell className="text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <UserRound className="mt-0.5 h-4 w-4 text-slate-500" />
                          <div className="space-y-1">
                            <div className="font-medium text-gray-900">{permit.requestor}</div>
                            <div className="text-xs text-gray-500">{permit.company}</div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="text-sm text-gray-700">
                        <div>{permit.workDateRange}</div>
                        <div className="text-xs text-gray-500">{permit.workTimeRange}</div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <Badge className={`${permit.statusClass} px-3 py-1 text-xs font-semibold`}>
                          {permit.statusLabel}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          {canEditPermit ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-indigo-500 hover:text-indigo-700"
                            onClick={() => onEditPermit(permit.recordId)}
                            title={isApprovalMode ? 'อนุมัติรายการ' : 'แก้ไข'}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          ) : null}

                          {!isApprovalMode ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-gray-500 hover:text-gray-800"
                              onClick={() => onViewPermit(permit.recordId)}
                              title="ดูรายละเอียด"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>

                      {!isApprovalMode ? (
                        <TableCell className="text-center">
                          {canPrintPermit ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => handleOpenPreview(permit.recordId)}
                            title="Print"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  )})
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
